import type { defaultOptions } from '../types/plugins/gtm';
import { createGtm, useGtm, GtmSupport } from '@gtm-support/vue-gtm';

declare module '@gtm-support/vue-gtm' {
  export interface GtmPlugin {
    track: (options: { [key: string]: string }) => void;
  }
}

declare module '#app' {
  interface NuxtApp {
    $gtm: GtmSupport;
  }
}

export default defineNuxtPlugin({
  name: 'gtm-plugin',
  setup(nuxtApp) {
  const { $auth, $config, $store, $api, $stage } = useNuxtApp();
  const router = useRouter();
  const route = useRoute();
  nuxtApp.pluginName = 'gtm-plugin';
  if (!($api.tenant && Object.keys($api.tenant)?.length)) {
    return;
  }

  const tenantConfig: any = $api.tenant;
  let gtmConfig = tenantConfig?.default?.gtm || {};
  const mustConsent = $store.state.gtmm?.mustConsent;
  const additionalAdobeTrack = tenantConfig?.default?.additionalAdobeTrack;
  let id = gtmConfig?.container;
  const additionalContainers = $store.state.gtmm?.additionalContainers || '';
  let pages = gtmConfig?.trackingPages;
  let gtmContainers = [] as any;

  if (additionalContainers?.length > 0) {
    gtmContainers = additionalContainers?.split(',');
    if (id) {
      gtmContainers.push(id);
    }
  } else if (id) {
    gtmContainers.push(id);
  }

  if (
    !$store.state.gtmm?.container &&
    gtmConfig &&
    Object.keys(gtmConfig)?.length
  ) {
    gtmConfig = { ...gtmConfig, container: gtmContainers };
    $store.commit('setIGtm', gtmConfig);
  }

  const GoogleAnalyticsVendorID = gtmConfig?.vendors?.GoogleAnalytics;
  const tenantTrackingScripts = gtmConfig?.trackingScripts || [];

  // Initialize gtm only when needed
  let gtm :GtmSupport | null = null;
  let customGtm : GtmSupport | any = {};
      customGtm = {
        track: (options: { [key: string]: string }) => {}
      }
  // Initialize tracking
   const defaultOptions: defaultOptions = {
     AF: $store.state.gtmm?.AF,
    //  EN: $stage,
    //  PR: 'Mondia',
    //  SE: 'MultiContent',
   };
  if (import.meta.client && typeof window !== 'undefined') {
    if (mustConsent) {
      checkForVendorConsent(GoogleAnalyticsVendorID);
    } else {
      initTracking();
    }
  }

  const uuid = $auth?.user?.UUID;
  if (uuid) {
    defaultOptions.UID = uuid;
    defaultOptions.AU = $auth.isLoggedIn
  }

  if ($store.state?.gtmm && Object.keys($store.state.gtmm)?.length) {
    id = $store.state.gtmm?.container;
    pages = $store.state.gtmm.trackingPages;
  }

  if (!pages) {
    pages = $config?.application?.base.gtm?.trackingPages;
  }

  router.afterEach((to) => {
    if (!import.meta.client && typeof window == 'undefined') {
      return;
    }

    if (mustConsent && localStorage?.getItem('analyticsConsent') !== '1') {
      return;
    }

    if (typeof gtm?.trackView === 'function') {
      setTimeout(() => {
        gtm?.trackView(
          pages[to.fullPath] || to.fullPath,
          to.fullPath,
          to.gtm || {
            pageUrl: to.fullPath,
            PSC: to.fullPath,
            // ET: 'page_view',
             PC: `${pages[to.fullPath] || to.fullPath}`,
            // PT: `View ${pages[to.fullPath] || to.fullPath}`,
            ...defaultOptions,
          },
        );
      }, 250);
    }
    adobeTrackFn('pageview', {}, to);
  });

  // Initialize the GTM instance
  function initTracking() {
    // Initialize GTM instance using `createGtm`
    nuxtApp.vueApp.use(
      createGtm({
        id: gtmContainers,
        defer: false,
        enabled: true,
        debug: false,
        trackViewEventProperty: 'page_view'
      }),
    );

    // Now safely use GTM instance
    gtm = useGtm() as GtmSupport;

    const setDefaultOptions = () => {
      const uuid = $auth?.user?.UUID;
      if (uuid) {
        defaultOptions.UID = uuid;
        defaultOptions.AU = $auth.isLoggedIn;
      }
    }

    // Ensure track method is available after initialization
    customGtm.track = (options) => {
      setDefaultOptions()
      if (!import.meta.client && typeof window == 'undefined') {
        return;
      }

      if (mustConsent && localStorage?.getItem('analyticsConsent') !== '1') {
        return;
      }

      if (!($store.state.gtmm && id)) {
        return;
      }

      const path = route.path;

      if (options.ET === 'Event' || options.EN === 'Event') {
        options.event = 'interaction';
      } else {
        options.event = options.ET;
      }

      if (pages[path]) {
        if (!options.EC && options.ET === 'Event') {
          options.EC = pages[path] || path;
        }
        if (!options.PC) {
          options.PC = pages[path] || path;
        }
      }

      let adobeTrack = options?.adobeTrack;
      if (adobeTrack && Object.keys(adobeTrack)?.length) {
        if (adobeTrack?.adobeData?.notification_area) {
          adobeTrack.adobeData.notification_area =
            options.PC || pages[path] || path;
        }
        adobeTrackFn(adobeTrack.adobeET, adobeTrack.adobeData);
      }

      gtm?.trackEvent({
        ...defaultOptions,
        ...options,
      });
    };
  }

  function adobeTrackFn(eventType, adobeData, to?) {
    if (eventType !== 'pageview') {
      return;
    }

    if (!additionalAdobeTrack) {
      return;
    }

    let path = to?.path ? to?.path : route?.path;
    const defaultAdobeDl = {
      login_status: `${
        $auth.user.roles[0] === 'user' ? 'logged in' : 'not logged in'
      }`,
      site_area: 'content services',
      page_levels: ['content services', ...convertPathToArray(path)],
      page_type: 'content',
    };

    if ($auth.user.roles[0] === 'user') {
      defaultAdobeDl['visitor_subscription'] = 'individual';
      defaultAdobeDl['visitor_type'] = 'unknown';
    }

    const platformType = getplatformType();
    if (platformType) {
      defaultAdobeDl['platform_type'] = platformType;
    }

    const _ddq = window._ddq || (window._ddq = []);
    _ddq.push([eventType, { ...defaultAdobeDl, ...adobeData }]);
  }

  function convertPathToArray(path) {
    const pageLevels = path.split('/');
    pageLevels.shift(); // Remove the first empty element if the path starts with '/'
    const result = [];
    for (let i = 0; i < pageLevels.length; i++) {
      const level = pageLevels[i];
      if (level && level.trim() !== '') {
        // Only push non-empty values
        result.push(level);
      }
    }
    return result.length ? result : ['home'];
  }

  function loadCookieScript(scripts) {
    localStorage?.setItem('tenantTrackingScriptsLoaded', '1');
    scripts?.forEach((script) => {
      const scriptTag = document.createElement('script');
      scriptTag.type = script?.type;
      scriptTag.async = script?.async;
      if (script?.src) {
        scriptTag.src = script?.src;
      }
      if (script?.innerHTML) {
        scriptTag.innerHTML = script?.innerHTML;
      }
      document?.head?.appendChild(scriptTag);
    });
  }

  function checkForVendorConsent(vendorid) {
    if (typeof window?.__tcfapi === 'function') {
      window?.__tcfapi('addEventListener', 2, function (tcData, success) {
        if (
          tcData?.eventStatus === 'tcloaded' ||
          tcData?.eventStatus === 'useractioncomplete'
        ) {
          window?.__tcfapi(
            'getCustomVendorConsents',
            2,
            function (data, success) {
              if (success) {
                const grants: any = data.grants;
                if (
                  grants?.hasOwnProperty(vendorid) &&
                  grants[vendorid]?.vendorGrant
                ) {
                  loadCookieScript(tenantTrackingScripts);
                  initTracking();
                  if (localStorage?.getItem('analyticsConsent') != '1') {
                    adobeTrackFn(
                      'pageview',
                      {},
                      router?.currentRoute.value.path,
                    );
                    localStorage?.setItem(
                      'analyticsConsent',
                      '1',
                    );
                    gtm?.trackView(
                      pages[router?.currentRoute.value.path] ||
                        router?.currentRoute.value.path,
                      router?.currentRoute.value.path,
                      {
                        pageUrl: router?.currentRoute.value.path,
                        // ET: 'page_view',
                        PC: `${pages[router?.currentRoute.value.path] || router?.currentRoute.value.path}`,
                        PT: `${pages[router?.currentRoute.value.path] || router?.currentRoute.value.path}`,
                        ...defaultOptions,
                      },
                    );
                  }
                  // if (!localStorage?.getItem('initTrackingLoaded')) {
                  //   location.reload();
                  //   localStorage?.setItem('initTrackingLoaded', '1');
                  // }
                  return;
                } else {
                  localStorage?.setItem('analyticsConsent', '0');
                  if (
                    localStorage?.getItem('tenantTrackingScriptsLoaded') == '1'
                  ) {
                    localStorage?.setItem('tenantTrackingScriptsLoaded', '0');
                    location.reload();
                  }
                  return;
                }
              }
            },
          );
        }
      });
    }
  }

  function getplatformType() {
    const platformTypes = $store?.state?.device?.platformTypes;
    if (!platformTypes) {
      return;
    }
    for (const key in platformTypes) {
      if (platformTypes[key])
        return PlatformTypes[key as keyof typeof PlatformTypes];
    }
    return;
  };
  return {
    provide: {
      customGtm: customGtm
    },
  };
}});

enum PlatformTypes {
  desktop = 'desktop',
  mobile = 'mobile',
  tablet = 'responsive',
}
